import React, { useCallback, useState } from 'react';
import { Notification } from '@ecosystem/newron-design-system/build/components/notifications/types';
import { CopyLinkActionProps, CopyLinkActionType } from '../../types';
import constants from '../../constants';
import { useAnalytics } from '../../../analytics';
import { getViewKey, useCreateDirectLink } from '../../hooks';
import { actions, elements } from '@ecosystem/newron-design-system';
import { ClientToast } from '../../../content';

const { Button, Menu } = actions;
const { Icon } = elements;

const { SAVE_TO_CLIPBOARD_ERROR_MESSAGE, SAVED_TO_CLIPBOARD_SUCCESS_MESSAGE, COPY_LINK_PURPOSE, VIEW_QUERY_PARAM } =
  constants;

function supportsClipBoardItem(): boolean {
  return Boolean(global.ClipboardItem);
}

export function CopySavedViewLinkAction<TViewState extends object>({
  createViewStateFn,
  defaultDataType,
  getTrackingTags,
  actionType,
}: CopyLinkActionProps<TViewState>) {
  const { track } = useAnalytics();
  const { createLink } = useCreateDirectLink();
  const [toast, setToast] = useState<{ type: Notification; message: string }>();

  const handleCreateCopyLink = useCallback(async () => {
    // Safari treats user activation differently so need this workaround to
    // give the user permissions to use the clipboard API
    // https://bugs.webkit.org/show_bug.cgi?id=222262
    try {
      // Keep this is a promise because apple locks down the clipboard api
      // when you await before you use it
      const viewLinkPromise = createLink(createViewStateFn, {
        dataType: defaultDataType || 'static',
        purpose: COPY_LINK_PURPOSE,
      }).then(({ url }) => url);

      if (supportsClipBoardItem()) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/plain': new Promise(resolve => {
              viewLinkPromise
                .then(viewLink => {
                  resolve(new Blob([viewLink], { type: 'text/plain' }));
                })
                .catch(e => {
                  setToast({ type: 'error', message: SAVE_TO_CLIPBOARD_ERROR_MESSAGE });
                });
            }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(await viewLinkPromise);
      }

      setToast({ type: 'success', message: SAVED_TO_CLIPBOARD_SUCCESS_MESSAGE });
      const trackingTags = getTrackingTags?.();
      const elementCategory = 'Copy Link';
      const elementName = 'Copy Link';
      const elementType = 'Platform';

      track('Page Element Clicked', {
        elementCategory,
        elementName,
        elementType,
        viewKey: getViewKey(await viewLinkPromise),
        ...trackingTags,
      });
    } catch (errorMessage) {
      setToast({ type: 'error', message: SAVE_TO_CLIPBOARD_ERROR_MESSAGE });
    }
  }, [createLink, createViewStateFn, defaultDataType, getTrackingTags, setToast, track]);

  return (
    <>
      {toast && (
        <ClientToast type={toast.type} onComplete={() => setToast(undefined)}>
          {toast.message}
        </ClientToast>
      )}
      {actionType === CopyLinkActionType.Button && (
        <Button onClick={handleCreateCopyLink} testId="copy-link-button" mode="secondary">
          <Button.Icon>
            <Icon.icons.LinkHoriz />
          </Button.Icon>
          Copy link
        </Button>
      )}
      {/* Doing like this because design system adds px-3 class instead of px-2 on button when we do like {!showIconOnly && 'Copy link'} */}
      {actionType === CopyLinkActionType.ButtonIconOnly && (
        <Button onClick={handleCreateCopyLink} testId="copy-link-button" mode="secondary" label="Copy link button">
          <Button.Icon>
            <Icon.icons.LinkHoriz />
          </Button.Icon>
        </Button>
      )}
      {actionType === CopyLinkActionType.MenuItem && (
        <Menu.Item onSelect={handleCreateCopyLink} testId="copy-link-menu-item">
          <Icon>
            <Icon.icons.LinkHoriz />
          </Icon>
          Copy link
        </Menu.Item>
      )}
    </>
  );
}
